<template>
  <div class="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
    <div class="max-w-md w-full space-y-8">
      <!-- Header -->
      <div class="text-center">
        <div class="flex items-center justify-center mb-4">
          <img src="/logo.png" alt="Happy Alien" class="w-20 h-20 object-contain" />
        </div>
        <h2 class="text-3xl font-bold text-gray-900">Reset Password</h2>
        <p class="mt-2 text-gray-600">
          Enter your email and we'll send you a link to reset your password.
        </p>
      </div>

      <!-- Success State -->
      <div v-if="emailSent" class="card">
        <div class="text-center space-y-4">
          <div class="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <PhEnvelope class="w-8 h-8 text-green-600" weight="fill" />
          </div>
          <h3 class="text-lg font-semibold text-gray-900">Check your email</h3>
          <p class="text-gray-600">
            We've sent a password reset link to <strong>{{ email }}</strong>
          </p>
          <p class="text-sm text-gray-500">
            Didn't receive the email? Check your spam folder or
            <button @click="resetForm" class="text-primary hover:text-primary/80 font-medium">
              try again
            </button>
          </p>
          <router-link
            to="/login"
            class="inline-flex items-center text-sm font-medium text-primary hover:text-primary/80"
          >
            <PhArrowLeft class="w-4 h-4 mr-1" />
            Back to sign in
          </router-link>
        </div>
      </div>

      <!-- Form State -->
      <div v-else class="card">
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <!-- Error Message -->
          <div v-if="error" class="rounded-xl p-4 border bg-red-50 border-red-200">
            <div class="flex">
              <PhWarning class="w-5 h-5 flex-shrink-0 text-red-400" weight="fill" />
              <div class="ml-3">
                <p class="text-sm text-red-800">{{ error }}</p>
              </div>
            </div>
          </div>

          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium mb-1 text-gray-700">
              Email address
            </label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              class="input"
              placeholder="Enter your email"
              :disabled="isLoading"
            />
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="isLoading"
            class="w-full btn-primary flex items-center justify-center"
          >
            <div
              v-if="isLoading"
              class="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"
            ></div>
            {{ isLoading ? "Sending..." : "Send reset link" }}
          </button>

          <!-- Back to login -->
          <div class="text-center">
            <router-link
              to="/login"
              class="inline-flex items-center text-sm font-medium text-primary hover:text-primary/80"
            >
              <PhArrowLeft class="w-4 h-4 mr-1" />
              Back to sign in
            </router-link>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { PhWarning, PhEnvelope, PhArrowLeft } from "@phosphor-icons/vue";

const email = ref("");
const isLoading = ref(false);
const error = ref("");
const emailSent = ref(false);

const apiUrl = (import.meta as any).env.VITE_API_URL || "http://localhost:3001";

async function handleSubmit() {
  if (!email.value) {
    error.value = "Please enter your email address";
    return;
  }

  isLoading.value = true;
  error.value = "";

  try {
    const response = await fetch(`${apiUrl}/auth/reset-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email: email.value }),
    });

    const data = await response.json();

    if (data.success) {
      emailSent.value = true;
    } else {
      error.value = data.error || "Failed to send reset email";
    }
  } catch (err) {
    error.value = "An unexpected error occurred. Please try again.";
    console.error("Password reset error:", err);
  } finally {
    isLoading.value = false;
  }
}

function resetForm() {
  emailSent.value = false;
  error.value = "";
}
</script>
